import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Instagram, Facebook } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Footer = () => {
  const { t, language } = useLanguage();
  
  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About */}
          <div>
            <h3 className="text-xl font-bold mb-4 text-teal-400">MR Excursion</h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              {t('footerAboutText')}
            </p>
            <div className="flex gap-3 mt-4">
              <a 
                href="https://www.facebook.com/share/183na4462t/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-500 transition-colors"
              >
                <Facebook size={18} />
              </a>
              <a 
                href="https://www.instagram.com/mr.excursion?utm_source=qr&igsh=MTYyYW5sNHo5bWFldA==" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-500 transition-colors"
              >
                <Instagram size={18} />
              </a>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-xl font-bold mb-4 text-teal-400">{t('footerContact')}</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-gray-400 text-sm">
                <Phone size={18} className="text-teal-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p>+1 (809) 765-6789</p>
                  <p className="text-xs text-gray-500">{language === 'es' ? 'WhatsApp disponible' : 'WhatsApp available'}</p>
                </div>
              </li>
              <li className="flex items-start gap-3 text-gray-400 text-sm">
                <Mail size={18} className="text-teal-400 mt-0.5 flex-shrink-0" />
                <a href="mailto:mendezmaikolmendez@gmail.com" className="hover:text-teal-400 transition-colors">
                  mendezmaikolmendez@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-3 text-gray-400 text-sm">
                <Clock size={18} className="text-teal-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p>{language === 'es' ? 'Lunes - Domingo' : 'Monday - Sunday'}</p>
                  <p>8:00 AM - 10:00 PM</p>
                </div>
              </li>
            </ul>
          </div>

          {/* Location */}
          <div>
            <h3 className="text-xl font-bold mb-4 text-teal-400">{language === 'es' ? 'Ubicación' : 'Location'}</h3>
            <div className="flex items-start gap-3 text-gray-400 text-sm">
              <MapPin size={18} className="text-teal-400 mt-0.5 flex-shrink-0" />
              <div>
                <p>Punta Cana, Bávaro</p>
                <p>La Altagracia</p>
                <p>{language === 'es' ? 'República Dominicana' : 'Dominican Republic'}</p>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold mb-4 text-teal-400">{t('footerLinks')}</h3>
            <ul className="space-y-2">
              <li>
                <a href="#inicio" className="text-gray-400 text-sm hover:text-teal-400 transition-colors">
                  {t('inicio')}
                </a>
              </li>
              <li>
                <a href="#excursiones" className="text-gray-400 text-sm hover:text-teal-400 transition-colors">
                  {t('excursiones')}
                </a>
              </li>
              <li>
                <a href="#villas" className="text-gray-400 text-sm hover:text-teal-400 transition-colors">
                  {t('villas')}
                </a>
              </li>
              <li>
                <a href="#contacto" className="text-gray-400 text-sm hover:text-teal-400 transition-colors">
                  {t('contacto')}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-2">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} MR Excursion Punta Cana. {t('footerRights')}.
            </p>
            <p className="text-gray-500 text-sm">
              {language === 'es' ? 'Creado por' : 'Created by'} <span className="text-teal-400 font-medium">_impulso365</span>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

const WhatsAppButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const phoneNumber = '18097656789';
  const message = 'Hola buenas! Vengo desde su catálogo web y me gustaría confirmar disponibilidad para una excursión en Punta Cana, así como recibir indicaciones para formalizar la reserva.';

  const handleWhatsAppClick = () => {
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
    // Trigger Google Ads conversion tracking
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {'send_to': 'AW-11168126798/-5UkCLa8v-IbEM6usM0p'});
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Popup Card */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 w-72 bg-white rounded-lg shadow-2xl overflow-hidden mb-2 animate-fade-in">
          {/* Header */}
          <div className="bg-green-500 p-4">
            <button 
              onClick={() => setIsOpen(false)}
              className="absolute top-2 right-2 text-white/80 hover:text-white text-xl font-bold"
            >
              ×
            </button>
            <h3 className="text-white font-bold">¡Hola! 👋</h3>
            <p className="text-white/90 text-sm mt-1">
              Haga click para chatear en WhatsApp
            </p>
          </div>
          
          {/* Contact */}
          <div className="p-3">
            <button
              onClick={handleWhatsAppClick}
              className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 font-bold text-lg">MR</span>
              </div>
              <div className="text-left">
                <p className="text-xs text-gray-500">Ejecutivo</p>
                <p className="font-medium text-gray-800">MR Excursion</p>
              </div>
            </button>
          </div>
          
          {/* Footer */}
          <div className="bg-gray-50 px-4 py-2 text-center">
            <p className="text-xs text-gray-500">Tours y Excursiones en Punta Cana</p>
          </div>
        </div>
      )}

      {/* WhatsApp Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-14 h-14 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
      >
        {isOpen ? (
          <span className="text-2xl font-bold">×</span>
        ) : (
          <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        )}
      </button>
    </div>
  );
};

export { Footer, WhatsAppButton };
